import React from "react";
import { FaEdit, FaSave, FaTimes, FaCheck, FaUndo } from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
} from "../UI/DesignStandards";
import { calculateTotalAllocated } from "./payoutUtils";
import {
  payoutTotalsMatchFund,
  wholeDollarFundTarget,
} from "../../utility/generateShapedPayoutTable";

const PayoutActionButtons = ({
  editMode,
  setEditMode,
  verificationLoading,
  isFinalized,
  isResultsFinalized,
  allTeamsVerified,
  localEventDetails,
  unsavedChanges,
  setUnsavedChanges,
  originalPlacesToPay,
  setLocalEventDetails,
  refreshEventDetails,
  refetchVerification,
  tournamentId,
  user,
  firstEventDetails,
}) => {
  const handleEditClick = () => {
    setEditMode(true);
    // Initialize unsaved changes with current values
    const initialValues = {};
    Object.entries(localEventDetails).forEach(([eventId, details]) => {
      if (details && typeof details.currentPayoutQuantity !== "undefined") {
        initialValues[eventId] = {
          payoutRatioOverride: details.currentPayoutQuantity,
        };
      }
    });
    setUnsavedChanges(initialValues);
  };

  const handleSaveChanges = async () => {
    try {
      // Validate total allocated amounts
      for (const [eventId, details] of Object.entries(localEventDetails)) {
        if (!details.selectedPayout || !details.selectedPayout.payouts) {
          throw new Error(
            `Event ${details.eventName} has no payout configuration selected`
          );
        }

        const totalAllocated = calculateTotalAllocated(
          details.selectedPayout.payouts
        );
        const prizeFund = details.prizeFund;
        const roundToWholeDollars = details.roundToWholeDollars !== false;

        if (
          !payoutTotalsMatchFund(totalAllocated, prizeFund, roundToWholeDollars)
        ) {
          const payable = roundToWholeDollars
            ? wholeDollarFundTarget(prizeFund)
            : prizeFund;
          throw new Error(
            `Event ${details.eventName} has $${totalAllocated.toFixed(
              2
            )} allocated. Places must total $${payable.toFixed(
              2
            )} and cannot go over the prize fund ($${Number(prizeFund).toFixed(2)}).`
          );
        }
      }

      // Save all unsaved changes to the database
      // Use selectedPayout which contains the saved/edited amounts, not possiblePayouts
      for (const [eventId, changes] of Object.entries(unsavedChanges)) {
        const details = localEventDetails[eventId];
        
        // Use selectedPayout which has the saved amounts (including any manual edits)
        if (details.selectedPayout && details.selectedPayout.payouts) {
          // Delete existing payouts for this event
          const { error: deleteError } = await supabase
            .from("tournamentPayouts")
            .delete()
            .eq("eventID", eventId);

          if (deleteError) throw deleteError;

          // Insert new payouts using the saved/edited amounts
          const payoutsToInsert = details.selectedPayout.payouts.map(
            (payout) => ({
              eventID: eventId,
              place: payout.place,
              percentage: payout.percentage,
              amount: Math.round(payout.amount * 100) / 100,
              createdBy: user.id,
              updatedAt: new Date().toISOString(),
            })
          );

          const { error: insertError } = await supabase
            .from("tournamentPayouts")
            .insert(payoutsToInsert);

          if (insertError) throw insertError;

          // Update the payout ratio override
          const { error } = await supabase
            .from("tournamentEvents")
            .update({
              payoutRatioOverride:
                changes.payoutRatioOverride === details.defaultPlacesToPay
                  ? null
                  : changes.payoutRatioOverride,
            })
            .eq("id", eventId);

          if (error) throw error;
        }
      }

      // Clear unsaved changes
      setUnsavedChanges({});

      // Refresh event details
      await refreshEventDetails();

      // Close edit mode
      setEditMode(false);
    } catch (error) {
      console.error("Error saving changes:", error);
      alert(error.message);
    }
  };

  const handleCancel = () => {
    // Revert all unsaved changes
    const revertedDetails = { ...localEventDetails };
    Object.entries(unsavedChanges).forEach(([eventId]) => {
      revertedDetails[eventId] = {
        ...revertedDetails[eventId],
        placesToPay: originalPlacesToPay[eventId],
      };
    });

    // Update local event details with original values
    setLocalEventDetails(revertedDetails);

    // Clear unsaved changes
    setUnsavedChanges({});

    // Close the modal
    setEditMode(false);
  };

  const handleFinalizeClick = async () => {
    try {
      // Check if results are finalized using the new verification system
      if (isResultsFinalized) {
        notify.error("Cannot finalize payouts when results are finalized");
        return;
      }

      // Check if all teams have verified scores before allowing payout finalization
      const { data: teams, error: teamsError } = await supabase
        .from("teams")
        .select(
          `
          id,
          squads!inner(tournamentID)
        `
        )
        .eq("squads.tournamentID", tournamentId)
        .eq("isDeleted", false);

      if (teamsError) throw teamsError;

      // Check if all teams have verified scores
      const { data: verificationStates, error: verificationError } =
        await supabase
          .from("verificationStates")
          .select("entityId, isVerified")
          .eq("tournamentId", tournamentId)
          .eq("entityType", "scores");

      if (verificationError) throw verificationError;

      const verifiedTeamIds = verificationStates
        .filter((vs) => vs.isVerified)
        .map((vs) => vs.entityId);

      const unverifiedTeams = teams.filter(
        (team) => !verifiedTeamIds.includes(team.id)
      );

      if (unverifiedTeams.length > 0) {
        notify.error(
          `Cannot finalize payouts. ${unverifiedTeams.length} team(s) still need verified scores.`
        );
        return;
      }

      // Save all current payouts to tournamentPayouts
      // Use selectedPayout which contains the saved amounts, not possiblePayouts which are calculated
      for (const [eventId, details] of Object.entries(localEventDetails)) {
        // Use selectedPayout which has the saved amounts from the database
        const selectedPayout = details.selectedPayout;

        if (selectedPayout && selectedPayout.payouts) {
          // Delete existing payouts for this event
          const { error: deleteError } = await supabase
            .from("tournamentPayouts")
            .delete()
            .eq("eventID", eventId);

          if (deleteError) throw deleteError;

          // Insert new payouts using the saved amounts
          const payoutsToInsert = selectedPayout.payouts.map((payout) => ({
            eventID: eventId,
            place: payout.place,
            percentage: payout.percentage,
            amount: Math.round(payout.amount * 100) / 100, // Round to nearest cent
            createdBy: user.id,
            updatedAt: new Date().toISOString(),
          }));

          const { error: insertError } = await supabase
            .from("tournamentPayouts")
            .insert(payoutsToInsert);

          if (insertError) throw insertError;
        }
      }

      // Use UPDATE instead of upsert since NULL entityId causes issues with onConflict
      // First, find the existing payout verification state
      const { data: existingState, error: fetchError } = await supabase
        .from("verificationStates")
        .select("id")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "payouts")
        .is("entityId", null) // Use .is() for NULL comparison instead of .eq()
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      let result;
      if (existingState) {
        // Update existing row
        result = await supabase
          .from("verificationStates")
          .update({
            isVerified: true,
            verifiedAt: new Date().toISOString(),
            verifiedBy: user.id,
          })
          .eq("id", existingState.id);
      } else {
        // Insert new row if none exists
        result = await supabase.from("verificationStates").insert({
          tournamentId,
          entityType: "payouts",
          entityId: null, // Tournament-level entity - ensure this is actual null
          isVerified: true,
          verifiedAt: new Date().toISOString(),
          verifiedBy: user.id,
        });
      }

      const { error } = result;

      if (error) throw error;

      // Refetch verification status to update UI
      if (refetchVerification) {
        refetchVerification();
      }
    } catch (error) {
      console.error("Error finalizing payouts:", error);
      notify.error("Failed to finalize payouts");
    }
  };

  const handleReverseFinalizeClick = async () => {
    try {
      // Check if results are finalized using the new verification system
      if (isResultsFinalized) {
        notify.error("Cannot unlock payouts when results are finalized");
        return;
      }

      // Use UPDATE instead of upsert since NULL entityId causes issues with onConflict
      // First, find the existing payout verification state
      const { data: existingState, error: fetchError } = await supabase
        .from("verificationStates")
        .select("id")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "payouts")
        .is("entityId", null) // Use .is() for NULL comparison instead of .eq()
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      let result;
      if (existingState) {
        // Update existing row
        result = await supabase
          .from("verificationStates")
          .update({
            isVerified: false,
            verifiedAt: null,
            verifiedBy: null,
            unverifiedAt: new Date().toISOString(),
            unverifiedBy: user.id,
          })
          .eq("id", existingState.id);
      } else {
        // Insert new row if none exists
        result = await supabase.from("verificationStates").insert({
          tournamentId,
          entityType: "payouts",
          entityId: null, // Tournament-level entity - ensure this is actual null, not string "null"
          isVerified: false,
          verifiedAt: null,
          verifiedBy: null,
          unverifiedAt: new Date().toISOString(),
          unverifiedBy: user.id,
        });
      }

      const { data, error } = result;

      if (error) throw error;

      // Refetch verification status to update UI
      if (refetchVerification) {
        await refetchVerification();
      }
    } catch (error) {
      console.error("Error reversing payout finalization:", error);
      notify.error("Failed to unlock payouts");
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {!editMode ? (
        <>
          {!isFinalized && (
            <SecondaryButton
              onClick={handleEditClick}
              disabled={verificationLoading}
              icon={FaEdit}
              className="!px-3 !py-2 text-sm"
            >
              {verificationLoading ? "Loading..." : "Edit Payouts"}
            </SecondaryButton>
          )}
          {!isFinalized ? (
            <div className="relative group">
              <PrimaryButton
                onClick={handleFinalizeClick}
                disabled={
                  verificationLoading || isResultsFinalized || !allTeamsVerified
                }
                icon={FaCheck}
              >
                {verificationLoading ? "Loading..." : "Lock Payouts"}
              </PrimaryButton>
              {!allTeamsVerified && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-brand-charcoal text-brand-cream text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  All teams must have verified scores before locking payouts
                </div>
              )}
            </div>
          ) : (
            <div className="relative group">
              <DangerButton
                onClick={
                  isResultsFinalized ? undefined : handleReverseFinalizeClick
                }
                disabled={isResultsFinalized || verificationLoading}
                icon={FaUndo}
              >
                {verificationLoading ? "Unlocking..." : "Unlock Payouts"}
              </DangerButton>
              {isResultsFinalized && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-brand-charcoal text-brand-cream text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Standings are finalized
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <PrimaryButton
            onClick={handleSaveChanges}
            disabled={verificationLoading}
            icon={FaSave}
          >
            {verificationLoading ? "Saving..." : "Save Changes"}
          </PrimaryButton>
          <SecondaryButton
            onClick={handleCancel}
            disabled={verificationLoading}
            icon={FaTimes}
            className="!px-3 !py-2 text-sm"
          >
            {verificationLoading ? "Loading..." : "Cancel"}
          </SecondaryButton>
        </>
      )}
    </div>
  );
};

export default PayoutActionButtons;
